import { useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, Divider, MenuItem, Stack, Typography, Tooltip } from '@mui/material';
import { CropFree as NoLicenseAssignmentIcon, WarningAmber as ExpiredSubscriptionIcon } from '@mui/icons-material';
import { DropdownProps, Dropdown } from '@localstack/ui';
import { Subscription } from '@localstack/types';

import { LicenseAssignmentPlanLabel } from './LicenseAssignmentPlanLabel';
import { SeatAvailability } from './types';

const EXPIRED_SUBSCRIPTION_WARNING = 'This license assignment points to an expired subscription.';

export interface LicenseAssignmentDropdownProps {
  allSubscriptions: Record<string, Subscription>;
  availableSubscriptions: Record<string, Subscription>;
  availableSubscriptionSeats: SeatAvailability;
  assignedSubscriptions: string[];
  onChangeAssignments: (subscriptionIds: string[]) => Promise<void>;
}

export const LicenseAssignmentDropdown = ({
  allSubscriptions,
  availableSubscriptions,
  availableSubscriptionSeats,
  assignedSubscriptions,
  onChangeAssignments,
}: LicenseAssignmentDropdownProps): JSX.Element => {
  const [isBusy, setIsBusy] = useState(false);

  const assignedAvailableSubscriptions = useMemo(
    () => Object.values(availableSubscriptions).filter((sub) => assignedSubscriptions.includes(sub.id)),
    [assignedSubscriptions, Object.keys(availableSubscriptions)],
  );

  const assignedExpiredSubscriptions = useMemo(
    () =>
      assignedSubscriptions
        .filter((subId) => !availableSubscriptions[subId])
        .map((subId) => allSubscriptions[subId])
        .filter((sub): sub is Subscription => !!sub),
    [assignedSubscriptions, Object.keys(availableSubscriptions), Object.keys(allSubscriptions)],
  );

  const visibleAssignedSubscriptionIds = useMemo(
    () => [...assignedAvailableSubscriptions, ...assignedExpiredSubscriptions].map((sub) => sub.id),
    [assignedAvailableSubscriptions, assignedExpiredSubscriptions],
  );

  const toggleLicenseAssignment = useCallback(
    async (subId: string) => {
      setIsBusy(true);
      const nextAssignments = visibleAssignedSubscriptionIds.includes(subId)
        ? visibleAssignedSubscriptionIds.filter((id) => id !== subId)
        : [...visibleAssignedSubscriptionIds, subId];
      await onChangeAssignments(nextAssignments);
      setIsBusy(false);
    },
    [visibleAssignedSubscriptionIds, onChangeAssignments],
  );

  const isAlreadyAssigned = (subId: string) => assignedSubscriptions.includes(subId);
  const hasNoSeatsLeft = (subId: string) => (availableSubscriptionSeats[subId]?.free ?? 0) <= 0;
  // A user may only hold one active license assignment per emulator type: block picking a
  // second product of the same emulator until the currently assigned one is removed.
  const hasEmulatorConflict = (subId: string) =>
    [...assignedAvailableSubscriptions, ...assignedExpiredSubscriptions].some(
      (sub) => sub.id !== subId && sub.plan.emulator === availableSubscriptions[subId]?.plan.emulator,
    );
  const hasUnselectableOptions = Object.values(availableSubscriptions).some(
    (subscription) => !isAlreadyAssigned(subscription.id) && hasEmulatorConflict(subscription.id),
  );

  return (
    <div>
      <Dropdown
        size="small"
        disabled={isBusy}
        placement="bottom-start"
        menuListProps={{
          disablePadding: true,
          style: { display: 'flex', gap: '1px', flexDirection: 'column', minWidth: '270px' },
        }}
        renderButton={({ onClick }: DropdownProps) => (
          <Button onClick={onClick}>
            {assignedAvailableSubscriptions.length === 0 && assignedExpiredSubscriptions.length === 0 ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <NoLicenseAssignmentIcon
                  sx={{ height: (theme) => theme.spacing(2), color: (theme) => theme.palette.text.secondary }}
                />
                <Typography variant="caption">No License</Typography>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" spacing={2}>
                {assignedAvailableSubscriptions.map((subscription) => (
                  <LicenseAssignmentPlanLabel
                    key={subscription.id}
                    emulator={subscription.plan.emulator}
                    family={subscription.plan.family}
                    hideTier
                  />
                ))}
                {assignedExpiredSubscriptions.map((subscription) => (
                  <Stack key={subscription.id} direction="row" alignItems="center" sx={{ color: 'warning.main' }}>
                    <LicenseAssignmentPlanLabel
                      emulator={subscription.plan.emulator}
                      family={subscription.plan.family}
                      hideTier
                      icon={
                        <Tooltip title={EXPIRED_SUBSCRIPTION_WARNING}>
                          <ExpiredSubscriptionIcon sx={{ height: (theme) => theme.spacing(2) }} />
                        </Tooltip>
                      }
                    />
                  </Stack>
                ))}
              </Stack>
            )}
          </Button>
        )}
      >
        {Object.values(availableSubscriptions).map((subscription) => (
          <MenuItem
            key={subscription.id}
            selected={assignedSubscriptions.includes(subscription.id)}
            disabled={
              isBusy ||
              (!isAlreadyAssigned(subscription.id) && hasNoSeatsLeft(subscription.id)) ||
              hasEmulatorConflict(subscription.id)
            }
            onClick={() => toggleLicenseAssignment(subscription.id)}
          >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
              <Checkbox
                size="small"
                disableRipple
                checked={isAlreadyAssigned(subscription.id)}
                sx={{ p: 0, mr: 0.5 }}
              />
              <LicenseAssignmentPlanLabel emulator={subscription.plan.emulator} family={subscription.plan.family} />
              <div style={{ flexGrow: 1 }} />
              <Typography variant="caption">{availableSubscriptionSeats[subscription.id]?.free ?? 0} left</Typography>
            </Stack>
          </MenuItem>
        ))}
        {assignedExpiredSubscriptions.map((subscription) => (
          <MenuItem
            key={subscription.id}
            selected
            disabled={isBusy}
            sx={{ color: 'warning.main' }}
            onClick={() => toggleLicenseAssignment(subscription.id)}
          >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
              <Checkbox
                size="small"
                disableRipple
                checked={isAlreadyAssigned(subscription.id)}
                sx={{ p: 0, mr: 0.5 }}
              />
              <LicenseAssignmentPlanLabel emulator={subscription.plan.emulator} family={subscription.plan.family} />
              <div style={{ flexGrow: 1 }} />
              <Tooltip title={EXPIRED_SUBSCRIPTION_WARNING}>
                <ExpiredSubscriptionIcon sx={{ height: (theme) => theme.spacing(2) }} />
              </Tooltip>
              <Typography variant="caption">Expired</Typography>
            </Stack>
          </MenuItem>
        ))}
        {hasUnselectableOptions && (
          <>
            <Divider />
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <Typography variant="caption" color="text.secondary">
                One license per emulator allowed
              </Typography>
            </MenuItem>
          </>
        )}
      </Dropdown>
    </div>
  );
};
